import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { getDocument, GlobalWorkerOptions, version as pdfjsVersion } from 'pdfjs-dist';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import html2canvas from 'html2canvas';
import 'pdfjs-dist/web/pdf_viewer.css';
import { formatDate } from '../utils/dateUtils';

// Worker setup
if (!GlobalWorkerOptions.workerSrc) {
  GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';
}
fetch('/pdf.worker.mjs', { method: 'HEAD' }).catch(() => {
  GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsVersion}/pdf.worker.min.js`;
});

const PdfFormEditor = forwardRef(({ pdfFile, width = 800, onDataChange, style = {}, dateField = 'date' }, ref) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fields, setFields] = useState([]);
  const [pageCanvases, setPageCanvases] = useState([]);
  const [scale, setScale] = useState(1.0);
  const [activePdfLabel, setActivePdfLabel] = useState(pdfFile);

  // Helper: PDF rect to viewport
  const pdfRectToViewportRect = (rect, viewport) => {
    const [llx, lly, urx, ury] = rect;
    const x = llx * viewport.scale;
    const y = (viewport.height - ury * viewport.scale);
    const w = (urx - llx) * viewport.scale;
    const h = (ury - lly) * viewport.scale;
    return { x, y, width: w, height: h };
  };

  // Load PDF and extract fields
  const loadPdf = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(pdfFile);
      if (!response.ok) throw new Error(`Failed to fetch PDF at ${pdfFile}`);
      const data = await response.arrayBuffer();
      const pdf = await getDocument({ data }).promise;
      const tempPages = [];
      const tempFields = [];
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        // Calculate scale to fit desired width
        const origViewport = page.getViewport({ scale: 1.0 });
        const desiredWidth = typeof width === 'number' ? width : parseInt(width, 10) || 800;
        const pageScale = desiredWidth / origViewport.width;
        const viewport = page.getViewport({ scale: pageScale });
        // Render page to canvas
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport }).promise;
        tempPages.push({ pageNum, canvas, width: viewport.width, height: viewport.height });
        // Extract annotations (widgets for form fields)
        const annotations = await page.getAnnotations();
        annotations.filter(a => a.subtype === 'Widget').forEach(a => {
          const rect = pdfRectToViewportRect(a.rect, viewport);
          let type = a.fieldType || 'text';
          if (type === 'Btn') {
            if (a.checkBox) type = 'checkbox';
            else if (a.radioButton) type = 'radio';
            else type = 'button';
          }
          let value = a.fieldValue || '';
          if ((type === 'date' || /date/i.test(a.fieldName || '') || a.fieldName === dateField) && !value) {
            // Format date as DD/MM/YYYY
            value = formatDate(new Date());
          }
          tempFields.push({
            name: a.fieldName || a.fullName || `field_${tempFields.length}`,
            page: pageNum,
            rect,
            value,
            type,
            checked: type === 'checkbox' ? (a.fieldValue === 'Yes' || a.fieldValue === true) : undefined
          });
        });
      }
      setPageCanvases(tempPages);
      setFields(tempFields);
      setActivePdfLabel(pdfFile);
      setLoading(false);
      if (onDataChange) onDataChange(tempFields);
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  }, [pdfFile, scale, onDataChange]);

  useEffect(() => {
    loadPdf();
  }, [loadPdf]);

  // Field change handler
  const handleChange = (name, value) => {
    setFields(prev => {
      const updated = prev.map(f => {
        if (f.name !== name) return f;
        if (f.type === 'checkbox') {
          return { ...f, value, checked: value === 'Yes' };
        }
        return { ...f, value };
      });
      if (onDataChange) onDataChange(updated);
      return updated;
    });
  };

  // Helper function to format amount with 2 decimal places
  const handleAmountChange = (name, value) => {
    // Allow normal typing - just store the raw value
    handleChange(name, value);
  };

  // Helper function to format amount when user finishes typing
  const handleAmountBlur = (name, value) => {
    // Only format if there's a value
    if (value && value.trim() !== '') {
      // Remove any non-numeric characters except decimal point
      const cleanValue = value.replace(/[^\d.]/g, '');

      // Parse as number and format to 2 decimal places
      const numValue = parseFloat(cleanValue);
      if (!isNaN(numValue)) {
        const formattedValue = numValue.toFixed(2);
        handleChange(name, formattedValue);
      }
    }
  };

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    getData: () => fields,
    getDataJson: () => fields.reduce((acc, f) => { acc[f.name] = f.type === 'checkbox' ? (f.checked ? 'Yes' : 'Off') : f.value; return acc; }, {}),
    async downloadPdf() {
      try {
        const response = await fetch(pdfFile);
        const existingPdfBytes = await response.arrayBuffer();
        const pdfDoc = await PDFDocument.load(existingPdfBytes);

        // Register fontkit for custom font support
        pdfDoc.registerFontkit(fontkit);

        const form = pdfDoc.getForm();

        // Load the Japanese font - this is required for CJK character support
        let japaneseFont = null;
        try {
          const fontResponse = await fetch('/fonts/NotoSansCJKjp-Regular.ttf');
          if (fontResponse.ok) {
            const fontBytes = await fontResponse.arrayBuffer();
            japaneseFont = await pdfDoc.embedFont(fontBytes, { subset: true });
          } else {
            console.warn('Japanese font file not found at /fonts/NotoSansCJKjp-Regular.ttf');
          }
        } catch (fontError) {
          console.warn('Failed to load Japanese font:', fontError);
        }

        // Helper function to check if text contains non-ASCII characters (Japanese, etc.)
        const hasNonAsciiChars = (text) => {
          if (!text) return false;
          // Match any character outside basic ASCII printable range
          return /[^\x00-\x7F]/.test(text);
        };

        fields.forEach(f => {
          try {
            if (f.type === 'checkbox') {
              const cb = form.getCheckBox(f.name);
              if (cb) {
                if (f.checked) cb.check(); else cb.uncheck();
              }
            } else if (f.type === 'text' || f.type === 'date' || f.type === 'Tx') {
              const tf = form.getTextField(f.name);
              if (tf) {
                // Always use Japanese font if available (it supports both ASCII and CJK)
                if (japaneseFont) {
                  // CORRECT ORDER: setText first, then updateAppearances
                  tf.setText(f.value || '');
                  tf.updateAppearances(japaneseFont);
                } else if (hasNonAsciiChars(f.value)) {
                  // Non-ASCII characters without Japanese font - skip to avoid WinAnsi error
                  console.warn(`Skipping field "${f.name}" - contains non-ASCII characters but Japanese font not available`);
                } else {
                  // ASCII-only text, safe to use default font
                  tf.setText(f.value || '');
                }
              }
            }
          } catch (e) {
            console.warn('Field error:', f.name, e);
          }
        });

        form.getFields().forEach(field => {
          if (field.constructor.name === 'PDFTextField') {
            const name = field.getName();
            const match = fields.find(f => f.name === name);
            if (match) {
              // Always use Japanese font if available
              if (japaneseFont) {
                // CORRECT ORDER: setText first, then updateAppearances
                field.setText(match.value || '');
                field.updateAppearances(japaneseFont);
              } else if (!hasNonAsciiChars(match.value)) {
                // Only set text if ASCII-only (safe for default font)
                field.setText(match.value || '');
              }
            }
          }
          if (field.constructor.name === 'PDFCheckBox') {
            const name = field.getName();
            const match = fields.find(f => f.name === name);
            if (match) {
              if (match.checked) field.check(); else field.uncheck();
            }
          }
        });

        // Flatten the form to prevent save() from calling updateFieldAppearances with default font
        form.flatten();

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'filled.pdf';
        link.click();
      } catch (e) {
        alert('PDF download failed: ' + e.message);
      }
    },
    async getPdfFile() {
      const response = await fetch(pdfFile);
      const existingPdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(existingPdfBytes);

      // Register fontkit for custom font support
      pdfDoc.registerFontkit(fontkit);

      const form = pdfDoc.getForm();

      // Load the Japanese font - required for CJK character support
      let japaneseFont = null;
      try {
        const fontResponse = await fetch('/fonts/NotoSansCJKjp-Regular.ttf');
        if (fontResponse.ok) {
          const fontBytes = await fontResponse.arrayBuffer();
          japaneseFont = await pdfDoc.embedFont(fontBytes, { subset: false });
        } else {
          console.error('Japanese font file not found at /fonts/NotoSansCJKjp-Regular.ttf');
          throw new Error('Japanese font file not found - required for form submission');
        }
      } catch (fontError) {
        console.error('Failed to load Japanese font:', fontError);
        throw new Error('Failed to load Japanese font: ' + fontError.message);
      }

      if (!japaneseFont) {
        throw new Error('Japanese font is required but could not be loaded');
      }

      try {
        // Process all form fields
        fields.forEach(f => {
          try {
            if (f.type === 'checkbox') {
              const cb = form.getCheckBox(f.name);
              if (cb) {
                if (f.checked) cb.check(); else cb.uncheck();
              }
            } else if (f.type === 'text' || f.type === 'date' || f.type === 'Tx') {
              const tf = form.getTextField(f.name);
              if (tf) {
                const textValue = f.value || '';
                tf.setText(textValue);
                tf.updateAppearances(japaneseFont);
              }
            }
          } catch (e) {
            console.warn('Field processing error for:', f.name, e);
          }
        });

        // Process any remaining fields
        form.getFields().forEach(field => {
          try {
            if (field.constructor.name === 'PDFTextField') {
              const name = field.getName();
              const match = fields.find(f => f.name === name);
              if (match && match.value) {
                field.updateAppearances(japaneseFont);
              }
            } else if (field.constructor.name === 'PDFCheckBox') {
              const name = field.getName();
              const match = fields.find(f => f.name === name);
              if (match) {
                if (match.checked) field.check(); else field.uncheck();
              }
            }
          } catch (e) {
            console.warn('Error processing field:', field.getName(), e);
          }
        });
      } catch (error) {
        console.error('PDF form filling error:', error);
        throw error;
      }

      form.flatten();
      const pdfBytes = await pdfDoc.save();
      return new Blob([pdfBytes], { type: 'application/pdf' });
    },
    async downloadImage() {
      if (containerRef.current) {
        const pdfArea = containerRef.current;
        const canvas = await html2canvas(pdfArea, {
          backgroundColor: '#fff',
          useCORS: true,
          scale: window.devicePixelRatio || 1
        });
        const link = document.createElement('a');
        link.download = 'pdf_capture.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    }
  }), [fields, pdfFile]);

  return (
    <div ref={containerRef} style={{ width, position: 'relative', ...style }}>
      {loading && <span>Loading...</span>}
      {error && <span style={{ color: 'tomato' }}>Error: {error}</span>}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
        {pageCanvases.map(p => (
          <div key={p.pageNum} style={{ position: 'relative', width: p.width, height: p.height }}>
            {p.canvas && <div ref={el => { if (el && !el.firstChild) el.appendChild(p.canvas); }} />}
            {fields.filter(f => f.page === p.pageNum).map(f => {
              if (f.type === 'checkbox') {
                return (
                  <div
                    key={f.name}
                    style={{
                      position: 'absolute',
                      left: f.rect.x,
                      top: f.rect.y,
                      height: f.rect.height,
                      display: 'flex',
                      alignItems: 'center',
                      pointerEvents: 'auto',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!f.checked}
                      onChange={e => handleChange(f.name, e.target.checked ? 'Yes' : 'Off')}
                      style={{
                        width: Math.min(f.rect.width, f.rect.height),
                        height: Math.min(f.rect.width, f.rect.height),
                        accentColor: '#007bff',
                        margin: 0,
                        boxSizing: 'border-box',
                        verticalAlign: 'middle',
                      }}
                    />
                  </div>
                );
              }
              if ((f.type === 'text' && /date/i.test(f.name)) || f.type === 'date' || f.name.trim().toLowerCase() === 'date' || f.name === dateField) {
                // Always use today's date and format it as DD/MM/YYYY
                const today = new Date();
                const formattedDate = formatDate(today);

                // Set the field value to today's date if it hasn't been set already
                if (f.value !== formattedDate) {
                  handleChange(f.name, formattedDate);
                }

                return (
                  <input
                    key={f.name}
                    type="text"
                    value={formattedDate}
                    readOnly
                    style={{
                      position: 'absolute',
                      left: f.rect.x,
                      top: f.rect.y,
                      width: f.rect.width,
                      height: f.rect.height,
                      border: '1px solid #999',
                      fontSize: Math.min(f.rect.height * 0.6, 16),
                      padding: 2,
                      boxSizing: 'border-box',
                      background: 'rgba(255,255,255,0.1)',
                      color: '#333',
                      cursor: 'default',
                    }}
                  />
                );
              }
              // Check if this is an amount field
              const isAmountField = /amount|offering|total|price|cost|fee|payment|money|sum|value/i.test(f.name) ||
                /Total Oering|Total_Offerings|fill_9/i.test(f.name);

              // fallback: text input
              return (
                <input
                  key={f.name}
                  value={f.value}
                  onChange={e => isAmountField ? handleAmountChange(f.name, e.target.value) : handleChange(f.name, e.target.value)}
                  onBlur={e => isAmountField ? handleAmountBlur(f.name, e.target.value) : undefined}
                  style={{
                    position: 'absolute',
                    left: f.rect.x,
                    top: f.rect.y,
                    width: f.rect.width,
                    height: f.rect.height,
                    background: 'rgba(255,255,255,0)',
                    border: '1px solid #999',
                    fontSize: Math.min(f.rect.height * 0.6, 16),
                    padding: 2,
                    boxSizing: 'border-box',
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
});

export default PdfFormEditor;
